import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import DefaultMap from '@/pages/notification/bio/DefaultMap.tsx'
import { format } from 'date-fns'
import ContactBadge from '@/component/badge/ContactBadge.tsx'
import { equalString, isEmpty } from '@/util/strings.ts'
import Input from '@/component/input/Input.tsx'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import useQueryGetUserDetail from '@/api/auth/getUser.ts'
import useQueryGetMe from '@/api/auth/getMe.ts'
import { getApiErrorMessage } from '@/api/api.ts'
import { isAlertStateConflict } from '@/util/emergencyAlertLiveSync.ts'
import useQueryGetAlertEmergencyDetail from '@/api/notification/getAlertEmergencyDetail.ts'
import useQueryGetFallDetectionDetail from '@/api/notification/getFallDetectionDetail.ts'
import { EmergencyLiveAlertContext } from '@/pages/notification/emergency/EmergencyAlertModal.tsx'
import { EmergencyAlertConfirmStatus } from '@/api/notification/getNotificationSos.ts'
import { FallDetectionResultType } from '@/api/notification/getNotificationFallDetection.ts'
import {
    apiPatchEmergencyAlertAssignPerson,
    apiPatchEmergencyAlertProcess
} from '@/api/notification/patchEmergencyAlert.ts'
import {
    apiPatchFallDetectionAlertAssignPerson,
    apiPatchFallDetectionAlertProcess
} from '@/api/notification/patchFallDetectionAlert.ts'
import { useTranslation } from 'react-i18next'
import { EmergencyAlertType } from '@/api/notification/getLiveEmergencyAlert.ts'
import { useMapLocationStore } from '@/store/MapLocationStore.ts'
import { useNavigate } from 'react-router-dom'
import LocationIcon from '@/assets/ic_alert_location.svg?react'
import SpinnerIcon from '@/assets/ic_loading.svg?react'
import { useAudioStore } from '@/store/AudioStore.ts'
import { ALERT_PROCESS_CONTENT_MAX_LENGTH } from '@/constant/alert.ts'
import { toast } from 'react-toastify'
import { formatAlertLocation, hasAlertCoordinates } from '@/util/alertLocation.ts'
import { getAlertGenderName } from '@/util/alertDisplay.ts'

export interface IProps {
    className?: string
}

const EmergencyAlertDetail: React.FC<IProps> = ({ className }) => {
    const { t } = useTranslation()
    const { stop: stopAudio } = useAudioStore()

    const { id, refetchList, refetchAndResetSelection, activeTab, isLiveMode, closeModal } = useContext(EmergencyLiveAlertContext)
    const { data: emergencyData, refetch: refetchEmergencyDetail, isLoading: isEmergencyLoading } = useQueryGetAlertEmergencyDetail(
        activeTab === EmergencyAlertType.SOS ? id : undefined
    )
    const { data: fallData, refetch: refetchFallDetail, isLoading: isFallLoading } = useQueryGetFallDetectionDetail(
        activeTab === EmergencyAlertType.FALL ? id : undefined
    )

    const remoteData = activeTab === EmergencyAlertType.SOS ? emergencyData : fallData
    // 목록 행 데이터와 상세 데이터를 혼합하지 않는다.
    // 선택된 ID의 상세 API 응답만 모달의 단일 데이터 원본으로 사용한다.
    const data = remoteData
    const isDetailLoading = activeTab === EmergencyAlertType.SOS ? isEmergencyLoading : isFallLoading
    const isLoading = id !== undefined && isDetailLoading
    const refetchDetail = activeTab === EmergencyAlertType.SOS ? refetchEmergencyDetail : refetchFallDetail

    const { data: user } = useQueryGetUserDetail(data?.protectee?.id)
    const { data: me } = useQueryGetMe()
    const [isExpandedMap, setExpandedMap] = useState(false)
    const navigate = useNavigate()
    const { setTargetLocation } = useMapLocationStore()

    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])
    
    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }
    
    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact
        
        // 모든 전화번호 앞 3자리만 보여주고 나머지는 * 처리
        const contactParts = contact.split('-')
        if (contactParts.length === 3) {
            return `${contactParts[0]}-****-****`
        }
        
        // 기타 패턴의 전화번호 처리
        if (contact.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }
        
        return contact
    }
    
    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    const alertLocation = data?.alert?.location
    const hasLocation = hasAlertCoordinates(alertLocation)
    const locationText = data?.alert ? formatAlertLocation(alertLocation, t, getMaskedAddress) : ''

    const actionTypes: ISelectOption[] = useMemo(
        () => [
            {
                id: FallDetectionResultType.ETC,
                text: t('notification.sosModal.type.etc'),
                value: FallDetectionResultType.ETC
            },
            {
                id: FallDetectionResultType.EMERGENCY_119,
                text: t('notification.sosModal.type.119'),
                value: FallDetectionResultType.EMERGENCY_119
            },
            {
                id: FallDetectionResultType.MALFUNCTION,
                text: t('notification.sosModal.type.malfunction'),
                value: FallDetectionResultType.MALFUNCTION

            }
        ],
        [t]
    )

    const [manager, setManager] = useState('')
    const [actionDetail, setActionDetail] = useState('')
    const [actionTypeId, setActionTypeId] = useState<string>(FallDetectionResultType.ETC)
    const [isSaving, setIsSaving] = useState(false)
    const isActionDetailTooLong = actionDetail.length > ALERT_PROCESS_CONTENT_MAX_LENGTH

    const resyncAfterConflict = async (error: unknown) => {
        if (!isLiveMode || !isAlertStateConflict(error)) return
        await refetchDetail()
        refetchList()
    }

    const onClickAssign = async () => {
        if (!id) return
        stopAudio()

        try {
            const { code } = activeTab === EmergencyAlertType.SOS
                ? await apiPatchEmergencyAlertAssignPerson(id, { manager_name: manager })
                : await apiPatchFallDetectionAlertAssignPerson(id, { manager_name: manager })

            if (code == 200) {
                if (!isLiveMode) {
                    closeModal()
                    return
                }
                await refetchDetail()
                refetchList()
            }
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('notification.process.saveError')))
            await resyncAfterConflict(error)
        }
    }

    const getGenderName = useCallback((value: string) => getAlertGenderName(t, 'sosModal', value), [t])

    const onClickProcessAction = async () => {
        if (!id) return
        if (!actionTypeId) return
        if (isEmpty(actionDetail) || isActionDetailTooLong || isSaving) {
            if (isActionDetailTooLong) {
                toast.error(t('notification.process.tooLong', { max: ALERT_PROCESS_CONTENT_MAX_LENGTH }))
            }
            return
        }
        stopAudio()

        setIsSaving(true)
        try {
            const { code, message } = activeTab === EmergencyAlertType.SOS
                ? await apiPatchEmergencyAlertProcess(id, {
                    task_result: actionTypeId,
                    task_content: actionDetail
                })
                : await apiPatchFallDetectionAlertProcess(id, {
                    task_result: actionTypeId,
                    task_content: actionDetail
                })

            if (Number(code) !== 200) throw new Error(message || t('notification.process.saveError'))
            toast.success(t('notification.process.saveSuccess'))
            if (!isLiveMode) {
                closeModal()
                return
            }
            const leftCurrent = await refetchAndResetSelection()
            if (!leftCurrent) await refetchDetail()
        } catch (error) {
            const message = error instanceof Error ? error.message : t('notification.process.saveError')
            toast.error(message || t('notification.process.saveError'))
            await resyncAfterConflict(error)
        } finally {
            setIsSaving(false)
        }
    }

    const onClickLocationButton = useCallback(() => {
        const latitude = data?.alert?.location?.latitude
        const longitude = data?.alert?.location?.longitude
        
        if (
            typeof latitude === 'number' && 
            typeof longitude === 'number' && 
            !isNaN(latitude) && 
            !isNaN(longitude) &&
            isFinite(latitude) && 
            isFinite(longitude)
        ) {
            setTargetLocation({
                latitude: latitude,
                longitude: longitude
            })
            navigate('/dashboard')
        } else {
            console.warn('Invalid location coordinates:', { latitude, longitude })
        }
    }, [data?.alert?.location, setTargetLocation, navigate])

    useEffect(() => {
        setActionTypeId(data?.alert?.result || FallDetectionResultType.ETC)
        setActionDetail(data?.alert?.content || '')
        setManager(me?.name || '')
    }, [data, me])

    const canEditContent = useMemo(() => {
        const confirmStatus = data?.alert?.confirm

        switch (confirmStatus) {
            case EmergencyAlertConfirmStatus.DONE:
                return true
            case EmergencyAlertConfirmStatus.ASSIGN:
                return !!data?.alert?.isMyTask
            case  EmergencyAlertConfirmStatus.WAIT:
            default:
                return false
        }
    }, [data])


    return <section className={`${className || ''} emergency-detail`}>
        {isLoading ? (
            <div className="emergency-detail__loading">
                <SpinnerIcon className="animate-spin w-8 h-8" />
            </div>
        ) : (
            <div className="emergency-detail__surface">
                <div className="emergency-detail__timeline">
                    <div>
                        <span>{t('notification.sos.table.emergency.column')}</span>
                        <strong>
                            {data?.alert?.createdAt ? format(data.alert.createdAt, 'yyyy/MM/dd HH:mm') : '-'}
                        </strong>
                    </div>
                    <div>
                        <span>{t('notification.sos.table.confirmed.column')}</span>
                        <strong>
                            {data?.alert?.checkedAt ? format(data.alert.checkedAt, 'yyyy/MM/dd HH:mm') : '-'}
                        </strong>
                    </div>
                </div>
                <div className="emergency-detail__grid">
                    <section className="emergency-detail__person-card">
                        <div className="emergency-detail__person">
                            <span>{getMaskedName(data?.protectee?.name)}</span>
                            <span>{getMaskedContact(data?.protectee?.contact)}</span>
                        </div>

                        <div className="emergency-detail__location">
                            <div className="emergency-detail__location-row">
                                <span 
                                    className="emergency-detail__address"
                                    onClick={() => hasLocation && setExpandedMap(!isExpandedMap)}>
                                    {locationText}
                                </span>
                                <div className="emergency-detail__location-actions">
                                    {data?.alert?.location?.latitude && data?.alert?.location?.longitude && (
                                        <button
                                            className="emergency-detail__location-button"
                                            onClick={onClickLocationButton}
                                            title={t('notification.v2.emergencyDetail.viewOnDashboard')}>
                                            <LocationIcon className={'w-4 h-4'} />
                                            {t('dashboard.notification.location')}
                                        </button>
                                    )}
                                    {hasLocation && (
                                        <button
                                            type="button"
                                            className="emergency-detail__map-toggle"
                                            onClick={() => setExpandedMap(!isExpandedMap)}
                                            aria-expanded={isExpandedMap}
                                        >
                                            {isExpandedMap ? t('notification.v2.map.collapse') : t('notification.v2.map.show')}
                                        </button>
                                    )}
                                </div>
                            </div>
                            {isExpandedMap && hasLocation && <DefaultMap
                                className={'min-h-[188px]'}
                                latitude={alertLocation.latitude}
                                longitude={alertLocation.longitude} />}
                        </div>
                        <div className="emergency-detail__meta">
                            <p><span>{t('notification.sosModal.address')}</span>{getMaskedAddress(user?.address) || t('common.empty')}</p>
                            <p><span>{t('notification.v2.emergencyDetail.protecteeInfo')}</span>{getGenderName(data?.protectee?.gender || '')} / {data?.protectee?.birthDate && format(data?.protectee?.birthDate, 'yy-MM-dd')}</p>
                        </div>

                        <div className="emergency-detail__section-heading">
                            <strong>{t('notification.sosModal.contact.title')}</strong>
                            <span>{t('notification.v2.emergencyDetail.registeredContacts')}</span>
                        </div>
                        <div className="emergency-detail__contacts">
                            <div>
                                <ContactBadge>{t('notification.sosModal.contact.me')}</ContactBadge>
                                <p>{getMaskedContact(data?.protectee?.contact)}</p>
                            </div>
                            <div>
                                <ContactBadge>{t('notification.sosModal.contact.protector')}</ContactBadge>
                                <p>{getMaskedContact(data?.protector?.contact)}</p>
                            </div>
                            <div>
                                <ContactBadge>{t('notification.sosModal.contact.acquaintance')}</ContactBadge>
                                <p>{getMaskedContact(data?.protector?.secondaryContact)}</p>
                            </div>
                            <div>
                                <ContactBadge>{t('notification.sosModal.contact.admin')}</ContactBadge>
                                <p>{getMaskedContact(data?.protector?.tertiaryContact)}</p>
                            </div>
                        </div>

                        <div className="emergency-detail__section-heading">
                            <strong>{t('notification.sosModal.memo.title')}</strong>
                            <span>{t('notification.v2.emergencyDetail.memoGuide')}</span>
                        </div>
                        <div className="emergency-detail__memo">
                            {isMaskingRequired ? '***************************' : data?.protectee?.memo}
                        </div>
                    </section>
                    <section className="emergency-detail__response-card">
                        <div className="emergency-detail__section-heading">
                            <strong>{t('notification.v2.emergencyDetail.responseTitle')}</strong>
                            <span>{t('notification.v2.emergencyDetail.responseDescription')}</span>
                        </div>
                        {!equalString(data?.alert?.confirm, EmergencyAlertConfirmStatus.WAIT) &&
                            <div className="emergency-detail__field">
                                <label>{t('notification.sosModal.manager.title')}</label>
                                <Input disabled className={'w-full'} value={getMaskedName(data?.alert?.managerName ?? undefined) || ''} />
                            </div>}
                        <div className="emergency-detail__field">
                            <label>{t('notification.sosModal.type.title')}</label>
                            <Select className={'w-full'}
                                    value={actionTypes?.find(item => item.id == actionTypeId)}
                                    options={actionTypes}
                                    disabled={!canEditContent}
                                    onChanged={option =>
                                        setActionTypeId(option?.id)
                                    } />
                        </div>
                        <div className={`emergency-detail__process ${!canEditContent ? 'is-disabled' : ''}`}>
                            <label>{t('notification.sosModal.process.title')}</label>
                            <textarea
                                      placeholder={t('notification.v2.emergencyDetail.processPlaceholder')}
                                      disabled={!canEditContent}
                                      maxLength={ALERT_PROCESS_CONTENT_MAX_LENGTH}
                                      value={actionDetail}
                                      onChange={e => setActionDetail(e.target.value)}
                                      aria-describedby="emergency-process-limit"
                            />
                            <div id="emergency-process-limit" className={`mt-[6px] flex justify-between gap-[8px] text-[11px] ${isActionDetailTooLong ? 'text-red-600' : 'text-[#7A8798]'}`} aria-live="polite">
                                <span>{isActionDetailTooLong ? t('notification.process.tooLong', { max: ALERT_PROCESS_CONTENT_MAX_LENGTH }) : t('notification.process.limitGuide', { max: ALERT_PROCESS_CONTENT_MAX_LENGTH })}</span>
                                <strong>{actionDetail.length} / {ALERT_PROCESS_CONTENT_MAX_LENGTH}</strong>
                            </div>
                        </div>
                    </section>
                </div>
                <footer className="emergency-detail__footer">
                    {/* 하단 버튼들 */}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.WAIT && <>
                        <div className="emergency-detail__assign">
                            <input
                                className="emergency-detail__manager-input"
                                placeholder={t('notification.sosModal.manager.input.placeholder')}
                                value={manager || ''}
                                onChange={e => {
                                    const text = e.target.value
                                    setManager(text)
                                }} />
                            <BorderButton text={t('notification.sosModal.manager.btn.title')}
                                          className="emergency-detail__assign-button"
                                          onClick={onClickAssign} />
                        </div>
                    </>}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.ASSIGN && data?.alert?.isMyTask && <>
                        <div className="emergency-detail__submit">
                            <SolidButton text={t('notification.sosModal.submit.confirm')} className="min-w-[200px]"
                                         disabled={isEmpty(actionDetail) || isEmpty(actionTypeId) || !canEditContent || isActionDetailTooLong || isSaving}
                                         onClick={onClickProcessAction} />
                        </div>
                    </>}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.ASSIGN && !data?.alert?.isMyTask &&
                        <div className="emergency-detail__submit">
                            <button className="emergency-detail__assigned"
                                    disabled>{t('notification.sosModal.manager.assign')}
                            </button>
                        </div>}
                    {data?.alert?.confirm == EmergencyAlertConfirmStatus.DONE && <>
                        <div className="emergency-detail__submit">
                            <SolidButton text={t('notification.sosModal.submit.save')} className="min-w-[200px]"
                                         disabled={isEmpty(actionDetail) || isEmpty(actionTypeId) || isActionDetailTooLong || isSaving}
                                         onClick={onClickProcessAction} />
                        </div>
                    </>}
                </footer>
            </div>
        )}
    </section>
}

export default EmergencyAlertDetail
